import { useCallback, useMemo } from "react"

import { useDispatch, useSelector } from "react-redux"

import { getCompareParams } from "../../../Components/Filters/CompareSelect/presets"
import { getDates } from "../../../Utils/utils"
import { SECTION_THUNKS } from "../redux/deflectAnalytics.thunks"
import { useDeflectFilters } from "./useDeflectFilters"

// The shared filters, spelled once as the three scopes a request can be read at, with a fetcher
// per scope. A section asks through the narrowest scope it needs, so its fetcher — and with it the
// section's effect — changes only when that scope does: flipping Compare refetches the blocks that
// show a comparison and leaves the rest alone.
//   getQueue   — the account only (the queues are the full current record set, RCA-6-03)
//   getBase    — the program and the calendar window
//   getCompare — + the comparison
// Each takes (key, params): the store key the answer lands in, and the section's own params.
// `page` is an inner page's route, for its own window; the Analytics page passes none.
export const useDeflectScope = (page) => {
  const dispatch = useDispatch()
  const {
    filters: { period, compare, program: programs }
  } = useDeflectFilters(page)
  const businessAccountId = useSelector((state) => state.auth.userInfo?.current_business_account_id)

  const program = programs?.length ? programs.join(",") : null

  // Not sent — the backend reads the account off the user — but a switch is a different set of
  // data, so every scope changes with it and every section refetches.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const accountScope = useMemo(() => ({}), [businessAccountId])

  const baseScope = useMemo(() => {
    const programScope = program ? { ...accountScope, program } : accountScope
    const { start_date, end_date } = getDates(period)
    return start_date && end_date ? { ...programScope, start_date, end_date } : programScope
  }, [accountScope, program, period])

  const compareScope = useMemo(
    () => ({ ...baseScope, ...getCompareParams(compare, getDates(period)) }),
    [baseScope, compare, period]
  )

  const getQueue = useCallback(
    (key, params) =>
      dispatch(SECTION_THUNKS[key]({ key, page, params: { ...accountScope, ...params } })),
    [dispatch, page, accountScope]
  )

  const getBase = useCallback(
    (key, params) =>
      dispatch(SECTION_THUNKS[key]({ key, page, params: { ...baseScope, ...params } })),
    [dispatch, page, baseScope]
  )

  const getCompare = useCallback(
    (key, params) =>
      dispatch(SECTION_THUNKS[key]({ key, page, params: { ...compareScope, ...params } })),
    [dispatch, page, compareScope]
  )

  // Whether the requests actually carry a comparison: a Compare picked over "All time" is dropped
  // by getCompareParams, so the filter alone does not say.
  const comparing = Boolean(compareScope.compare)

  return { getQueue, getBase, getCompare, comparing }
}
